import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { STOREFRONT_URL, MAPS_URL, cdnImg } from "../lib/data";

export default function DineIn() {
    return (
        <section
            data-testid="dine-in-section"
            className="relative py-20 md:py-28 bg-[#0B0A09] text-[#FAF8F4] overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    {/* Image — left on desktop, AFTER text on mobile */}
                    <motion.figure
                        initial={{ opacity: 0, scale: 1.03 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                        className="order-2 lg:order-1 lg:col-span-7 relative overflow-hidden"
                    >
                        <img
                            src={cdnImg(STOREFRONT_URL, 1200)}
                            alt="The Gaffer storefront at night — neon sign and mural-covered interior glowing through the glass"
                            loading="lazy"
                            decoding="async"
                            className="w-full h-auto object-cover aspect-[4/3] md:aspect-[5/4]"
                        />
                    </motion.figure>

                    {/* Mobile-only CTA row — sits UNDER the photo */}
                    <div className="order-3 lg:hidden grid grid-cols-2 gap-3 w-full">
                        <a
                            href={MAPS_URL}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="dine-in-directions-btn-mobile"
                            className="inline-flex items-center justify-center gap-2 bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#0B0A09] text-[#FAF8F4] px-4 py-4 text-[0.65rem] uppercase tracking-[0.2em] font-semibold transition-colors duration-300"
                        >
                            Directions
                            <svg
                                aria-hidden="true"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                className="w-4 h-4"
                            >
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                <circle cx="12" cy="10" r="3" />
                            </svg>
                        </a>
                        <Link
                            to="/book"
                            data-testid="dine-in-book-btn-mobile"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#FAF8F4]/30 hover:border-[#EB4A18] hover:text-[#EB4A18] text-[#FAF8F4] px-4 py-4 text-[0.65rem] uppercase tracking-[0.2em] font-semibold transition-colors duration-300"
                        >
                            Book a Table
                            <span aria-hidden="true">→</span>
                        </Link>
                    </div>

                    {/* Text — right on desktop, FIRST on mobile */}
                    <div className="order-1 lg:order-2 lg:col-span-5">
                        <motion.p
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.7 }}
                            className="text-xs uppercase tracking-[0.28em] font-semibold text-[#EB4A18] mb-4"
                        >
                            Dine In
                        </motion.p>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.08,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="font-display uppercase text-[clamp(2.4rem,5.5vw,4.5rem)] leading-[0.92]"
                        >
                            After dark
                            <br />
                            on{" "}
                            <span className="text-[#EB4A18]">
                                York Street.
                            </span>
                        </motion.h2>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.18,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="mt-8 space-y-5 text-[#C7C2B8] text-base md:text-lg leading-relaxed max-w-md"
                        >
                            <p>
                                Murals on every wall. Neon-blue Gaffer sign over
                                the bar. Low lights, loud music, and the smell
                                of brioche on the grill.
                            </p>
                            <p>
                                Pull up a stool, smash a burger, sink a beer.
                                Best seat in Albany after sunset — the kitchen’s
                                open till eight, the vibe runs late.
                            </p>
                        </motion.div>

                        <motion.dl
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.28,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="mt-10 grid grid-cols-2 gap-x-6 gap-y-6 max-w-sm border-t border-white/10 pt-8"
                        >
                            <Fact k="Mon – Sat" v="11am – 8pm" />
                            <Fact k="190 York St" v="Albany, WA" />
                        </motion.dl>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.9,
                                delay: 0.38,
                                ease: [0.19, 1, 0.22, 1],
                            }}
                            className="hidden lg:flex mt-10 gap-3 flex-wrap"
                        >
                            <a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="dine-in-directions-btn"
                                className="inline-flex items-center gap-3 bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#0B0A09] text-[#FAF8F4] px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors duration-300"
                            >
                                Get Directions
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="w-4 h-4"
                                >
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                            </a>
                            <Link
                                to="/book"
                                data-testid="dine-in-book-btn"
                                className="inline-flex items-center gap-3 bg-transparent border border-[#FAF8F4]/30 hover:border-[#EB4A18] hover:text-[#EB4A18] text-[#FAF8F4] px-7 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors duration-300"
                            >
                                Book a Table
                                <span aria-hidden="true">→</span>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Fact({ k, v }) {
    return (
        <div>
            <dt className="font-display uppercase text-2xl text-[#FAF8F4] leading-tight">
                {k}
            </dt>
            <dd className="mt-1 text-xs uppercase tracking-[0.22em] text-[#A39F98]">
                {v}
            </dd>
        </div>
    );
}
